import {
  useRef,
  useState,
  useEffect,
} from "react";
import { IGenerateReportResponse } from "@/_store/report/contracts";
import { useGenerateReportMutation } from "@/_store/report/report.slice";
import useShowToaster from "@/_hooks/Toasters/useShowToaster";
import InfoAlertIcon from "@/assets/icons/main/blueAlert.svg";
import AlertIcon from "@/assets/icons/main/redAlert.svg";
import { POLLING_INTERVAL_MS, STATUS_COMPLETE } from "@/_utils/contstans";

interface UseReportHandlerProps {
  id: string;
  isFiz: boolean;
}

const useReportHandler = ({ id, isFiz }: UseReportHandlerProps) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isPollingWhileGeneratingFile, setIsPollingWhileGeneratingFile] = useState<boolean>(false);
  const [IsShouldPollReportData, setIsShouldPollReportData] = useState<boolean>(false);
  const notificationShown = useRef<boolean>(false);

  const [generateReport] = useGenerateReportMutation();
  const showNotification = useShowToaster();

  useEffect(() => {
    if (pdfUrl) {
      const link: HTMLAnchorElement = document.createElement("a");
      link.href = pdfUrl;
      link.setAttribute("download", "report.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  }, [pdfUrl]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (isPollingWhileGeneratingFile) {
      intervalId = setInterval(async () => {
        try {
          const response: IGenerateReportResponse = await generateReport({ id, is_fiz: isFiz }).unwrap();

          if (response?.status === STATUS_COMPLETE) {
            setPdfUrl(response?.pdf_url);
            setIsPollingWhileGeneratingFile(false);
          }

        } catch (error: any) {
          setIsPollingWhileGeneratingFile(false);
          const errorMessage: string = error?.data?.error || "Ошибка при запросе отчета. Попробуйте позже.";

          showNotification({ message: errorMessage, icon: <AlertIcon /> });
        }
      }, POLLING_INTERVAL_MS);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [id, isFiz, showNotification, generateReport, isPollingWhileGeneratingFile]);

  const handleGenerate = async () => {
    showNotification({
      message: "Нужно немного времени для формирования отчета.",
      icon: <InfoAlertIcon />,
    });

    try {
      const response = await generateReport({ id, is_fiz: isFiz }).unwrap();

      if (response?.status === STATUS_COMPLETE) {
        setPdfUrl(response?.pdf_url);
      } else {
        setIsPollingWhileGeneratingFile(true);
      }

    } catch (error: any) {
      const errorMessage: string = error?.data?.error || "Ошибка при запросе отчета. Попробуйте позже.";

      showNotification({ message: errorMessage, icon: <AlertIcon /> });
    }
  };

  return {
    pdfUrl,
    isPollingWhileGeneratingFile,
    IsShouldPollReportData,
    setIsShouldPollReportData,
    handleGenerate,
    notificationShown,
  };
};

export default useReportHandler;
